import React from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import {
  arrowBackOutline,
  footballOutline,
  lockClosedOutline,
  peopleOutline,
  podiumOutline,
  swapVerticalOutline,
  timeOutline,
  trophyOutline,
} from "ionicons/icons";
import { useAuthStore } from "@/store/authStore";
import { useI18n } from "@/i18n";

const scoringRows = [
  {
    titleKey: "howItWorksExactTitle",
    ptsKey: "howItWorksExactPts",
    bodyKey: "howItWorksExactBody",
    color: "var(--app-score-exact)",
  },
  {
    titleKey: "howItWorksDiffTitle",
    ptsKey: "howItWorksDiffPts",
    bodyKey: "howItWorksDiffBody",
    color: "var(--app-score-diff)",
  },
  {
    titleKey: "howItWorksTendTitle",
    ptsKey: "howItWorksTendPts",
    bodyKey: "howItWorksTendBody",
    color: "var(--app-score-tendency)",
  },
  {
    titleKey: "howItWorksNoneTitle",
    ptsKey: "howItWorksNonePts",
    bodyKey: "howItWorksNoneBody",
    color: "var(--app-score-wrong)",
  },
] as const;

const roundKeys = [
  "howItWorksRound1",
  "howItWorksRound2",
  "howItWorksRound3",
  "howItWorksRound4",
  "howItWorksRound5",
  "howItWorksRound6",
  "howItWorksRound7",
] as const;

const HowItWorks: React.FC = () => {
  const { t } = useI18n();
  const { isAuthenticated } = useAuthStore();
  const backHref = isAuthenticated ? "/home" : "/";

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton routerLink={backHref} routerDirection="back">
              <IonIcon icon={arrowBackOutline} />
            </IonButton>
          </IonButtons>
          <IonTitle>{t("howItWorksNav")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="hero-section" style={{ padding: "32px 24px 28px" }}>
          <h1 style={{ fontSize: "26px" }}>{t("howItWorksTitle")}</h1>
          <p>{t("howItWorksLead")}</p>
          {!isAuthenticated && (
            <IonButton
              expand="block"
              routerLink="/register"
              routerDirection="forward"
              color="light"
              style={{
                "--border-radius": "10px",
                fontWeight: 700,
                marginTop: "16px",
                color: "var(--ion-color-primary)",
              }}
            >
              {t("howItWorksCtaRegister")}
            </IonButton>
          )}
        </div>

        <div className="rules-page">
          <section className="rules-section">
            <div className="rules-section-icon">
              <IonIcon icon={footballOutline} />
            </div>
            <h2>{t("howItWorksPlayTitle")}</h2>
            <p>{t("howItWorksPlayBody")}</p>
          </section>

          <section className="rules-section">
            <div className="rules-section-icon">
              <IonIcon icon={lockClosedOutline} />
            </div>
            <h2>{t("howItWorksDeadlineTitle")}</h2>
            <p>{t("howItWorksDeadlineBody")}</p>
          </section>

          <section className="rules-section">
            <div className="rules-section-icon">
              <IonIcon icon={trophyOutline} />
            </div>
            <h2>{t("howItWorksScoringTitle")}</h2>
            <p>{t("howItWorksScoringIntro")}</p>
            <div className="rules-score-list">
              {scoringRows.map((row) => (
                <div key={row.titleKey} className="rules-score-row">
                  <span
                    className="rules-score-pts"
                    style={{
                      background: row.color,
                      color: row.titleKey === "howItWorksExactTitle" ? "#000" : "#fff",
                    }}
                  >
                    {t(row.ptsKey)}
                  </span>
                  <div>
                    <strong>{t(row.titleKey)}</strong>
                    <span>{t(row.bodyKey)}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rules-section">
            <div className="rules-section-icon">
              <IonIcon icon={peopleOutline} />
            </div>
            <h2>{t("howItWorksLeaguesTitle")}</h2>
            <p>{t("howItWorksLeaguesBody")}</p>
            <div className="rules-pyramid" aria-hidden="true">
              <div className="rules-pyramid-row">
                <span>1. Tippliga</span>
              </div>
              <div className="rules-pyramid-row">
                <span>2. A</span>
                <span>2. B</span>
              </div>
              <div className="rules-pyramid-row">
                <span>3. A</span>
                <span>3. B</span>
                <span>3. C</span>
                <span>3. D</span>
              </div>
            </div>
          </section>

          <section className="rules-section">
            <div className="rules-section-icon">
              <IonIcon icon={swapVerticalOutline} />
            </div>
            <h2>{t("howItWorksJoinTitle")}</h2>
            <p>{t("howItWorksJoinBody")}</p>
          </section>

          <section className="rules-section">
            <div className="rules-section-icon">
              <IonIcon icon={timeOutline} />
            </div>
            <h2>{t("howItWorksRoundsTitle")}</h2>
            <p>{t("howItWorksRoundsBody")}</p>
            <ol className="rules-round-list">
              {roundKeys.map((key) => (
                <li key={key}>{t(key)}</li>
              ))}
            </ol>
          </section>

          <section className="rules-section">
            <div className="rules-section-icon">
              <IonIcon icon={podiumOutline} />
            </div>
            <h2>{t("howItWorksPromoTitle")}</h2>
            <p>{t("howItWorksPromoBody")}</p>
          </section>

          <section className="rules-section">
            <h2>{t("howItWorksHistoryTitle")}</h2>
            <p>{t("howItWorksHistoryBody")}</p>
          </section>

          <section className="rules-close">
            <h2>{t("howItWorksCloseTitle")}</h2>
            <p>{t("howItWorksCloseBody")}</p>
            {isAuthenticated ? (
              <IonButton
                expand="block"
                routerLink="/home"
                className="save-btn"
                style={{ "--border-radius": "10px", fontWeight: 600 }}
              >
                {t("home")}
              </IonButton>
            ) : (
              <>
                <IonButton
                  expand="block"
                  routerLink="/register"
                  routerDirection="forward"
                  className="save-btn"
                  style={{ "--border-radius": "10px", fontWeight: 600 }}
                >
                  {t("howItWorksCtaRegister")}
                </IonButton>
                <IonButton
                  expand="block"
                  fill="outline"
                  color="secondary"
                  routerLink="/login"
                  routerDirection="forward"
                  style={{ "--border-radius": "10px", fontWeight: 600 }}
                >
                  {t("howItWorksCtaLogin")}
                </IonButton>
              </>
            )}
          </section>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default HowItWorks;
